import { isNil } from 'lodash-es';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useAvailableBusinessesModal from '../../../hooks/useAvailableBusinessesModal';
import type { PartnerBusinessDescriptor } from '../../../redux/ducks/SearchDucks.types';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';

interface PartnerSeoSummaryProps {
  ssrFetchedData: AppSsrFetchedDataType;
  partnerBusinesses: PartnerBusinessDescriptor[];
  selectedBusiness: string | undefined;
}

const PartnerSeoSummary: React.FC<PartnerSeoSummaryProps> = ({
  partnerBusinesses,
  selectedBusiness,
}) => {
  const { t } = useTranslation();
  const availableBusinessesModal = useAvailableBusinessesModal();

  const targetBusiness = useMemo<PartnerBusinessDescriptor | undefined>(() => {
    if (!isNil(selectedBusiness)) {
      const goodBizs = partnerBusinesses.filter(
        (b) => b.active && b.id === selectedBusiness,
      );
      if (goodBizs.length > 0) {
        return goodBizs[0];
      }
    }

    return undefined;
  }, [selectedBusiness, partnerBusinesses]);

  const matchingBusinesses = useMemo<PartnerBusinessDescriptor[]>(() => {
    return partnerBusinesses.filter((b) => b.active && b.services.length > 0);
  }, [partnerBusinesses]);

  return (
    <div className="flex min-w-full flex-col items-start justify-start px-2">
      {targetBusiness ? (
        <div className="flex min-w-full flex-row items-start justify-start">
          <span className="flex flex-col items-start justify-start grow">
            <button
              type="button"
              id="partner-page-summary-more-businesses-title"
              key="partner-page-summary-more-businesses-title"
              aria-label={
                targetBusiness?.name
                  ? targetBusiness.name
                  : `${targetBusiness.location.address}, ${targetBusiness.location.city}`
              }
              data-test-id="partner-page-summary-more-businesses-title"
              data-umami-event="partner-page-summary-more-businesses-title-selected"
              data-umami-event-source="summary"
              className="line-clamp-1 grow cursor-pointer overflow-hidden text-ellipsis break-all text-xl font-bold capitalize underline sm:no-underline"
              onClick={availableBusinessesModal.onOpen}
            >
              {targetBusiness?.name
                ? targetBusiness.name
                : `${targetBusiness.location.address}, ${targetBusiness.location.city}`}
            </button>
            <span className="line-clamp-1 min-w-full overflow-hidden text-ellipsis break-all font-semibold capitalize">
              {`${targetBusiness.location.address}, ${targetBusiness.location.city}`}
            </span>
          </span>
          {matchingBusinesses.length > 1 && (
            <span className="hidden flex-col items-end justify-end sm:flex sm:min-w-[128px]">
              <button
                id="partner-page-summary-more-businesses-btn"
                key="partner-page-summary-more-businesses-btn"
                aria-label={t('static.generic.show_more_businesses')}
                data-test-id="partner-page-summary-more-businesses-btn"
                data-umami-event="partner-page-summary-more-businesses-btn-selected"
                data-umami-event-source="summary"
                className="cursor-pointer rounded-lg p-2 text-right text-sm font-semibold underline hover:bg-slate-100"
                type="button"
                onClick={availableBusinessesModal.onOpen}
              >
                {t('static.generic.show_more_businesses')}
              </button>
            </span>
          )}
        </div>
      ) : (
        <div className="min-w-full items-center justify-center hidden">
          <span className="text-sm text-slate-500 font-bold">
            {t('static.generic.show_no_businesses')}
          </span>
        </div>
      )}
    </div>
  );
};

export default PartnerSeoSummary;
